import type { Bounds, Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { Popup } from '../../popups/Popup';
import { DailyRewardPopupLayout } from '../../../layouts/popups/DailyRewardPopupLayout';
import { playLayoutAnimation } from '../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { DailyRewardPathHandler } from './DailyRewardPathHandler';
import { DailyRewardOfferButtonState, DailyRewardOfferButtonView } from './DailyRewardOfferButtonView';
import type { AppStateController } from '../../app/state/AppStateController';
import type { DailyRewardConfig } from '../../behaviours/openDailyRewardPopup';
import { formatAmountForInventory } from '../shopPage/utils/setIconForInventory';
import { InventoryItem } from '../../app/resources/types';
import type { ResourceAnimationsView } from '../../app/resources/animations/ResourceAnimationsView';
import type { MainMenuTopBarView } from '../MainMenuTopBarView';
import { applyLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';

interface DailyRewardPopupConfig {
  state: AppStateController;
  resourceAnimationsView: ResourceAnimationsView;
  rewardsConfig: DailyRewardConfig;
  topBarView: MainMenuTopBarView | null;
}

export class DailyRewardPopup implements Popup {
  public readonly onCloseClicked = new Signal();

  public readonly onCollectFreeClicked = new Signal();

  public readonly onCollectAdClicked = new Signal();

  private readonly layout = new DailyRewardPopupLayout();

  private readonly pathHandler: DailyRewardPathHandler;

  private readonly adButtons: DailyRewardOfferButtonView[] = [];

  public root: Container = this.layout.root;

  public initialBounds: Bounds = this.root.getLocalBounds();

  private activeAdOfferIndex: number;

  constructor(private readonly config: DailyRewardPopupConfig) {
    const { adRewardsOpened, freeRewardOpened } = config.state.getDailyRewardData();
    this.activeAdOfferIndex = adRewardsOpened;
    this.pathHandler = new DailyRewardPathHandler(this.layout, adRewardsOpened);
    this.init(config.rewardsConfig, freeRewardOpened);
  }

  private init(rewardsConfig: DailyRewardConfig, freeRewardOpened: boolean): void {
    const { goBackToMenuButton, adOfferButtonSlot, freeButton, adOfferAmount, freeOfferAmount } = this.layout;

    goBackToMenuButton.on('pointertap', () => {
      this.onCloseClicked.emit();
    });

    if (freeRewardOpened) {
      applyLayoutState(this.layout, 'noFreeReward');
      this.layout.buttonCollect.root.visible = false;
      this.layout.buttonCollectDisabled.root.visible = true;
      freeButton.interactive = false;
    } else {
      this.layout.buttonCollect.root.visible = true;
      this.layout.buttonCollectDisabled.root.visible = false;
      freeButton.on('pointertap', () => {
        this.onCollectFreeClicked.emit();
      });
    }

    adOfferButtonSlot.forEach((slot) => {
      const button = new DailyRewardOfferButtonView().props({
        parent: slot,
      });

      button.onClicked.connect(() => {
        this.onCollectAdClicked.emit();
      });
      this.adButtons.push(button);
    });
    this.refreshAdButtonState();

    freeOfferAmount.forEach((text) => {
      const [item, amount] = Object.entries(rewardsConfig.freeOffer)[0];
      formatAmountForInventory(text, item as InventoryItem, amount);
    });

    adOfferAmount.forEach((text, index) => {
      const offer = rewardsConfig.adOffers[index];
      if (!offer) return;
      const [item, amount] = Object.entries(offer)[0];
      formatAmountForInventory(text, item as InventoryItem, amount);
    });
  }

  public onShown(): void {
    void playLayoutAnimation(this.layout, 'Appear');
  }

  public async animateFreeRewardCollected(): Promise<void> {
    await this.animateRewardParticles(this.layout.freeOfferButtonSlot, true);
    applyLayoutState(this.layout, 'noFreeReward');
    this.layout.buttonCollect.root.visible = false;
    this.layout.buttonCollectDisabled.root.visible = true;
    this.layout.freeButton.interactive = false;
  }

  public async animateAdRewardCollected(): Promise<void> {
    const {
      config: { topBarView },
    } = this;

    this.activeAdOfferIndex += 1;
    this.pathHandler.openNextReward();
    this.refreshAdButtonState();

    if (!topBarView) return;

    const { start, coins } = this.getAdRewardParticleData(this.activeAdOfferIndex - 1);
    await this.animateRewardParticles(start, coins);
  }

  private getAdRewardParticleData(adIndex: number): { start: Container; coins: boolean } {
    const {
      config: { rewardsConfig },
    } = this;

    const start = this.layout.adOfferContainer[adIndex];
    const rewards = rewardsConfig.adOffers[adIndex];
    const coins = (Object.keys(rewards)[0] as InventoryItem) === InventoryItem.Coin;

    return { start, coins };
  }

  private animateRewardParticles(start: Container, coins: boolean): Promise<void> {
    const {
      config: { resourceAnimationsView, topBarView },
    } = this;

    if (!topBarView) return Promise.resolve();

    const target = (coins ? topBarView.moneyCounter : topBarView.livesCounter).icon;

    if (coins) {
      resourceAnimationsView.animateCoins(start, target);
    } else {
      resourceAnimationsView.animateUnlimitedLives(start, target);
    }

    return resourceAnimationsView.waitUntilAnimationsComplete();
  }

  private refreshAdButtonState(): void {
    this.adButtons.forEach((button, index) => {
      let state = DailyRewardOfferButtonState.Active;
      if (index < this.activeAdOfferIndex) state = DailyRewardOfferButtonState.Claimed;
      if (index > this.activeAdOfferIndex) state = DailyRewardOfferButtonState.Locked;
      button.setState(state);
    });
  }
}
